import { useState } from 'react'
import { Box, InputBase, IconButton } from '@mui/material'
import Search from '@assets/header_dashboard/Search.svg'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import { useAppTheme } from '@/theme/ThemeContext'

export default function SearchBar({
  placeholder = 'הקלד מספר מזהה, כרטיס מים או כתובת',
  defaultValue = '',
  onSearch,
  executeSearch,
  onClear,
  width = 380,
  height = 46,
  route,
  sx,
}: {
  placeholder?: string
  defaultValue?: string
  onSearch?: (value: string) => void
  width?: number | string
  height?: number
  route?: string
  executeSearch?: (value: string) => void
  /** Runs when the clear button is pressed, in place of `executeSearch('')`. */
  onClear?: () => void
  sx?: any
}) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    background: isDarkMode ? '#091327' : '#fff',
    border: isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)',
    text: isDarkMode ? '#FFFFFF' : 'rgba(0, 63, 97, 0.60)',
    iconColor: isDarkMode ? '#FFFFFF' : '#003F61',
    iconFilter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
  }

  const [searchValue, setSearchValue] = useState(defaultValue)
  const navigate = useNavigate()

  const runSearch = () => {
    executeSearch?.(searchValue)
    if (route) navigate(route)
  }

  const handleClear = () => {
    setSearchValue('')
    onSearch?.('')
    if (onClear) onClear()
    else executeSearch?.('')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        width: width,
        maxWidth: '100%',
        height: height,
        border: `1.2px solid ${colors.border}`,
        borderRadius: '5px',
        backgroundColor: colors.background,
        px: 1,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        ...sx
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: colors.iconColor,
          mr: 1,
          flexShrink: 0,
        }}
        onClick={runSearch}
      >
        <Box component="img" src={Search} alt="חיפוש" sx={{ width: 14, height: 14, display: 'block', filter: colors.iconFilter }} />
      </Box>

      <InputBase
        dir="rtl"
        value={searchValue}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.stopPropagation()
            runSearch()
          }
        }}
        onChange={e => {
          setSearchValue(e.target.value)
          onSearch?.(e.target.value)
        }}
        inputProps={{ dir: 'rtl' }}
        placeholder={placeholder}
        sx={{
          flex: 1,
          textAlign: 'right',
          fontFamily: 'Rubik',
          fontSize: height <= 32 ? '13px' : '15px',
          fontStyle: 'normal',
          fontWeight: 400,
          color: colors.text,
          minWidth: 0,
        }}
      />

      {searchValue && (
        <IconButton size="small" aria-label="נקה חיפוש" onClick={handleClear} sx={{ p: 0.3, color: colors.iconColor, flexShrink: 0 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  )
}
